// src/views/parent/PAOffres.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";
import Swal from "sweetalert2";

export default function PAOffres({ slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();
  const { getOffresDisponibles, getAbonnementsParent, souscrireOffre } = useParentService();

  const [offres, setOffres] = useState([]);
  const [abonnements, setAbonnements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🛡️ Réf anti-boucle / anti-StrictMode
  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT ──────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [offresData, abonnementsData] = await Promise.all([
        getOffresDisponibles(slug),
        getAbonnementsParent(slug).catch(() => []),
      ]);
      setOffres(offresData || []);
      setAbonnements(abonnementsData || []);
    } catch (err) {
      setError("Impossible de charger les offres.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const isAlreadySubscribed = (offreId) => {
    return abonnements.some((ab) => ab?.offre?.id === offreId && ab?.is_active === true);
  };

  const getAbonnementsForOffre = (offreId) => {
    return abonnements.filter((ab) => ab?.offre?.id === offreId && ab?.is_active === true);
  };

  const handleSouscrire = async (offre) => {
    // Demander pour quel enfant souscrire
    const enfants = abonnements.length > 0 ? abonnements.map(ab => ab.eleve).filter(Boolean) : [];
    const enfantsUniques = [];
    const seen = new Set();
    for (const e of enfants) {
      const key = e.id || e;
      if (!seen.has(key)) {
        seen.add(key);
        enfantsUniques.push(e);
      }
    }

    let eleveId = null;

    if (enfantsUniques.length > 1) {
      // Plusieurs enfants -> demander lequel
      const { value } = await Swal.fire({
        title: "Pour quel enfant ?",
        text: "Sélectionnez l'enfant pour lequel vous voulez souscrire",
        icon: "question",
        input: "select",
        inputOptions: enfantsUniques.reduce((acc, e) => {
          const nom = e.nom_complet || `${e.prenom} ${e.nom}` || `Enfant ${e.id}`;
          acc[e.id] = nom;
          return acc;
        }, {}),
        inputPlaceholder: "Choisissez un enfant",
        showCancelButton: true,
        confirmButtonText: "Souscrire",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#1B4F80",
        cancelButtonColor: "#6c757d",
      });

      if (!value) return;
      eleveId = parseInt(value);
    } else if (enfantsUniques.length === 1) {
      eleveId = enfantsUniques[0].id;
    }

    const result = await Swal.fire({
      title: "Confirmation",
      text: `Souscrire à l'offre "${offre.nom}" ${eleveId ? `pour ${enfantsUniques.find(e => e.id === eleveId)?.nom_complet || ''}` : ''} ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Souscrire",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#1B4F80",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      const payload = { offre: offre.id };
      if (eleveId) payload.eleve_id = eleveId;

      await souscrireOffre(slug, payload);

      await Swal.fire({
        title: "Succès",
        text: "Souscription réussie !",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
        toast: true,
      });

      // Recharger les données
      await loadData();
    } catch (err) {
      await Swal.fire({
        title: "Erreur",
        text: err.response?.data?.error || "Impossible de souscrire à cette offre",
        icon: "error",
        confirmButtonColor: "#1B4F80",
      });
    }
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .offres-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .offres-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 52px 16px 40px;
          text-align: center;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          overflow: hidden;
        }

        .offres-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .offres-header .menu-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }

        .offres-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .offres-header .icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        .offres-header .icon-wrapper i {
          font-size: 24px;
          color: #fff;
        }

        .offres-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 22px;
          font-weight: 700;
          margin: 0;
        }

        .offres-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.75);
          font-size: 14px;
          margin: 8px 0 0;
        }

        .offres-list {
          padding: 16px;
        }

        /* ─── LOADER intégré ─── */
        .offres-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }

        .offres-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .offres-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* ─── CARTE D'ERREUR (identique PANotifications) ─── */
        .offres-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }

        .offres-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .offres-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .offres-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .offres-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .offres-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .offres-error .btn:hover { opacity: 0.9; }

        .offres-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        /* ─── BOUTON VOIR ABONNEMENTS ─── */
        .offres-list .view-abonnements {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }

        .offres-list .view-abonnements .btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fff;
          border: 1.5px solid var(--pr);
          border-radius: 30px;
          padding: 10px 24px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: var(--pr);
          transition: all 0.2s;
        }

        .offres-list .view-abonnements .btn:hover {
          background: var(--pr);
          color: #fff;
        }

        .offres-list .view-abonnements .btn:hover i {
          color: #fff;
        }

        /* ─── CARTES OFFRE ─── */
        .offre-card {
          background: #fff;
          border-radius: 20px;
          border: 1px solid rgba(27,79,128,0.12);
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          margin-bottom: 14px;
          padding: 18px 20px;
          animation: fadeUp 0.4s ease both;
        }

        .offre-card .header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }

        .offre-card .header .icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--pr);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .offre-card .header .icon i {
          color: #fff;
          font-size: 18px;
        }

        .offre-card .header .title {
          flex: 1;
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: var(--t);
          margin: 0;
        }

        .offre-card .header .popular {
          background: var(--or-lt);
          padding: 3px 10px;
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          color: var(--pr-dk);
        }

        .offre-card .description {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          line-height: 1.5;
          margin: 0 0 14px;
        }

        .offre-card .chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 16px;
        }

        .offre-card .chips .chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: var(--s2);
          padding: 4px 12px;
          border-radius: 30px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: var(--m);
        }

        .offre-card .chips .chip i {
          font-size: 12px;
          color: var(--pr);
        }

        .offre-card .subscribed-info {
          margin-top: 8px;
          padding: 6px 12px;
          background: var(--pr-bg);
          border-radius: 8px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--pr);
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .offre-card .subscribed-info i {
          font-size: 14px;
        }

        .offre-card .subscribed-info .eleve-tag {
          background: rgba(27,79,128,0.1);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .offre-card .btn-subscribe {
          width: 100%;
          padding: 10px;
          border: none;
          border-radius: 30px;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 8px;
        }

        .offre-card .btn-subscribe.active {
          background: var(--gn);
        }

        .offre-card .btn-subscribe.active:hover {
          opacity: 0.9;
        }

        .offre-card .btn-subscribe.inactive {
          background: var(--h);
          cursor: default;
        }

        .offre-card .btn-subscribe.subscribed {
          background: var(--m);
          cursor: default;
        }

        /* ─── ÉTAT VIDE ─── */
        .offres-empty {
          text-align: center;
          padding: 40px 20px;
        }

        .offres-empty i {
          font-size: 48px;
          color: var(--h);
          opacity: 0.4;
        }

        .offres-empty h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--m);
          margin: 12px 0 4px;
        }

        .offres-empty p {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--h);
          margin: 0;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 480px) {
          .offres-header {
            padding: 44px 16px 32px;
          }
          .offres-header h1 {
            font-size: 19px;
          }
          .offre-card {
            padding: 14px 16px;
          }
          .offre-card .header {
            flex-wrap: wrap;
          }
          .offre-card .subscribed-info {
            font-size: 11px;
          }
        }
      `}</style>

      <div className="offres-page">
        {/* Header — TOUJOURS visible */}
        <div className="offres-header">
          <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
            <i className="ti ti-menu-2" />
          </button>

          <div className="icon-wrapper">
            <i className="ti ti-shopping-bag" />
          </div>
          <h1>Choisissez une offre</h1>
          <p>Découvrez les offres disponibles</p>
        </div>

        {/* Contenu */}
        <div className="offres-list">
          {loading ? (
            /* ── ÉTAT CHARGEMENT ── */
            <div className="offres-loading">
              <div className="spinner"></div>
              <span>Chargement des offres…</span>
            </div>
          ) : error ? (
            /* ── ÉTAT ERREUR (identique à PANotifications) ── */
            <div className="offres-error">
              <i className="ti ti-alert-circle alert-icon" />
              <h2>Erreur de chargement</h2>
              <p>{error}</p>
              <div className="actions-row">
                <button
                  className="btn"
                  onClick={() => {
                    hasFetchedRef.current = false; // ⬅️ autorise un nouveau fetch
                    loadData();
                  }}
                >
                  <i className="ti ti-refresh" />
                  Réessayer
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/${slug}/parent/accueil`)}
                >
                  <i className="ti ti-home" />
                  Accueil
                </button>
              </div>
            </div>
          ) : (
            /* ── CONTENU ── */
            <>
              {/* Bouton Voir mes abonnements */}
              {abonnements.length > 0 && (
                <div className="view-abonnements">
                  <button className="btn" onClick={() => navigate(`/${slug}/parent/abonnements`)}>
                    <i className="ti ti-calendar-check" />
                    Voir mes abonnements
                  </button>
                </div>
              )}

              {offres.length === 0 ? (
                <div className="offres-empty">
                  <i className="ti ti-shopping-bag" />
                  <h2>Aucune offre disponible</h2>
                  <p>Revenez plus tard pour découvrir nos offres</p>
                </div>
              ) : (
                offres.map((offre, index) => {
                  const dejaAbonne = isAlreadySubscribed(offre.id);
                  const actif = offre.actif ?? false;
                  const peutSouscrire = actif && !dejaAbonne;
                  const abonnementsOffre = getAbonnementsForOffre(offre.id);

                  return (
                    <div key={offre.id} className="offre-card" style={{ animationDelay: `${index * 0.06}s` }}>
                      <div className="header">
                        <div className="icon">
                          <i className="ti ti-star" />
                        </div>
                        <h3 className="title">{offre.nom || "Offre"}</h3>
                        {offre.populaire && (
                          <span className="popular">Populaire</span>
                        )}
                      </div>

                      <p className="description">{offre.description || "Aucune description"}</p>

                      <div className="chips">
                        <span className="chip">
                          <i className="ti ti-info-circle" />
                          Type : {offre.type_notification || "-"}
                        </span>
                        <span className="chip">
                          <i className="ti ti-coin" />
                          {offre.prix?.toLocaleString() || "0"} FCFA
                        </span>
                        <span className="chip">
                          <i className="ti ti-calendar" />
                          {offre.duree_jours || "-"} jours
                        </span>
                        <span className="chip">
                          <i className="ti ti-check-circle" />
                          Actif : {actif ? "Oui" : "Non"}
                        </span>
                      </div>

                      {dejaAbonne && abonnementsOffre.length > 0 && (
                        <div className="subscribed-info">
                          <i className="ti ti-user-check" />
                          <span>Déjà souscrit pour :</span>
                          {abonnementsOffre.map((ab) => {
                            const nom = ab.eleve_nom && ab.eleve_prenom
                              ? `${ab.eleve_prenom} ${ab.eleve_nom}`
                              : ab.eleve?.nom_complet || "Enfant";
                            return (
                              <span key={ab.id} className="eleve-tag">
                                {nom}
                                {ab.eleve_matricule && ` (${ab.eleve_matricule})`}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      <button
                        className={`btn-subscribe ${dejaAbonne ? 'subscribed' : actif ? 'active' : 'inactive'}`}
                        onClick={() => handleSouscrire(offre)}
                        disabled={!peutSouscrire}
                      >
                        {dejaAbonne ? "Déjà abonné" : actif ? "Souscrire" : "Non disponible"}
                      </button>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}